import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import type {
  AssessmentInput,
  AssessmentResultsView,
  AssessmentSummaryView,
  AssessmentView,
  AttemptSummaryView,
  CorrectResultInput,
  QuestionBankFacets,
  QuestionFacetsQuery,
  QuestionImportCheck,
  QuestionImportResult,
  QuestionInput,
  QuestionListQuery,
  QuestionView,
  RecordResultInput,
  SentTestView,
  TestAction,
} from '@nest-recruit/shared';
import { RECORDED_RESULT_FIELD, SCORESHEET_FIELD } from '@nest-recruit/shared';
import { apiDelete, apiGet, apiPost, apiPut, apiUrl, request, type ApiError } from '../../../lib/api-client';

/**
 * S17 — the bank (ASM-01), tests (ASM-02), a job's results (ASM-07) and sending a test
 * (F9.03). The realtime hints name `assessment-results`, `applications` and `jobs`, so
 * a candidate submitting on their phone refreshes the recruiter's open results.
 */

export const QUESTIONS_KEY = ['assessment-questions'] as const;
export const TESTS_KEY = ['assessment-tests'] as const;
export const RESULTS_KEY = ['assessment-results'] as const;

export type QuestionFilters = Omit<QuestionListQuery, 'page' | 'pageSize'> & { readonly page: number };

const BANK_PAGE_SIZE = 50;

export function useQuestions(filters: QuestionFilters): UseQueryResult<{ readonly rows: readonly QuestionView[]; readonly total: number }, ApiError> {
  return useQuery({
    queryKey: [...QUESTIONS_KEY, 'list', filters],
    queryFn: async () => {
      const result = await request<readonly QuestionView[]>('/assessments/questions', { query: { ...filters, pageSize: BANK_PAGE_SIZE } });
      return { rows: result.data, total: result.meta?.total ?? result.data.length };
    },
    // Never blank a list the viewer can already see (CLAUDE.md §5a rule 2).
    placeholderData: (previous) => previous,
  });
}

export const QUESTION_PAGE_SIZE = BANK_PAGE_SIZE;

/** The bank's filter options; with the list's filters, each option's count (every filter but its own). */
export function useQuestionFacets(filters: QuestionFacetsQuery = {}): UseQueryResult<QuestionBankFacets, ApiError> {
  return useQuery({
    queryKey: [...QUESTIONS_KEY, 'facets', filters],
    queryFn: () => apiGet<QuestionBankFacets>('/assessments/questions/facets', { query: { ...filters } }),
    placeholderData: (previous) => previous,
  });
}

export function useSaveQuestion() {
  const queryClient = useQueryClient();
  return useMutation<QuestionView, ApiError, { readonly id: string | null; readonly version: number; readonly ventureId: string; readonly question: QuestionInput }>({
    mutationFn: ({ id, version, ventureId, question }) =>
      id ? apiPut<QuestionView>(`/assessments/questions/${id}`, { version, question }) : apiPost<QuestionView>('/assessments/questions', { ventureId, question }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: QUESTIONS_KEY }), queryClient.invalidateQueries({ queryKey: TESTS_KEY })]);
    },
  });
}

/** F9.17 — the template's address; a plain link, since the session cookie rides along. */
export const QUESTION_TEMPLATE_URL = apiUrl('/assessments/questions/import-template');

function sheetBody(file: File): FormData {
  const body = new FormData();
  body.append('file', file);
  return body;
}

/** F9.17, step one: which rows are good. Saves nothing. */
export function useCheckQuestionFile() {
  return useMutation<QuestionImportCheck, ApiError, File>({
    mutationFn: async (file) => (await request<QuestionImportCheck>('/assessments/questions/import-check', { method: 'POST', body: sheetBody(file) })).data,
  });
}

/** F9.17, step two: the same file again; its good rows become questions. */
export function useImportQuestionFile() {
  const queryClient = useQueryClient();
  return useMutation<QuestionImportResult, ApiError, { readonly file: File; readonly ventureId: string }>({
    mutationFn: async ({ file, ventureId }) =>
      (await request<QuestionImportResult>('/assessments/questions/import', { method: 'POST', body: sheetBody(file), query: { ventureId } })).data,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUESTIONS_KEY });
    },
  });
}

interface QuestionPage {
  readonly rows: readonly QuestionView[];
  readonly total: number;
}
type Snapshot<T> = readonly (readonly [QueryKey, T | undefined])[];

/** Archiving can't be undone, so it keeps its confirm; once confirmed the row leaves at once (CLAUDE.md §5a rule 1). */
export function useArchiveQuestion() {
  const queryClient = useQueryClient();
  const lists = { queryKey: [...QUESTIONS_KEY, 'list'] };
  return useMutation<unknown, ApiError, { readonly id: string; readonly version: number }, { readonly before: Snapshot<QuestionPage> }>({
    mutationFn: ({ id, version }) => apiDelete(`/assessments/questions/${id}`, { version }),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(lists);
      const before = queryClient.getQueriesData<QuestionPage>(lists);
      queryClient.setQueriesData<QuestionPage>(lists, (page) =>
        page && page.rows.some((row) => row.id === id) ? { rows: page.rows.filter((row) => row.id !== id), total: Math.max(0, page.total - 1) } : page,
      );
      return { before };
    },
    onError: (_error, _variables, context) => restore(queryClient, context?.before),
    // Held until the refetch lands, so the row never flickers back (CLAUDE.md §5a rule 3).
    onSettled: () => queryClient.invalidateQueries({ queryKey: QUESTIONS_KEY }),
  });
}

function restore<T>(queryClient: QueryClient, before: Snapshot<T> | undefined): void {
  for (const [key, data] of before ?? []) queryClient.setQueryData(key, data);
}

export function useTests(ventureId?: string): UseQueryResult<readonly AssessmentSummaryView[], ApiError> {
  return useQuery({
    queryKey: [...TESTS_KEY, 'list', ventureId ?? 'all'],
    queryFn: () => apiGet<readonly AssessmentSummaryView[]>('/assessments', ventureId ? { query: { ventureId } } : undefined),
    placeholderData: (previous) => previous,
  });
}

export function useTest(id: string | null): UseQueryResult<AssessmentView, ApiError> {
  return useQuery({
    queryKey: [...TESTS_KEY, 'detail', id],
    queryFn: () => apiGet<AssessmentView>(`/assessments/${id ?? ''}`),
    enabled: id !== null,
  });
}

export function useSaveTest() {
  const queryClient = useQueryClient();
  return useMutation<AssessmentView, ApiError, { readonly id: string | null; readonly version: number; readonly ventureId: string; readonly assessment: AssessmentInput }>({
    mutationFn: ({ id, version, ventureId, assessment }) =>
      id ? apiPut<AssessmentView>(`/assessments/${id}`, { version, assessment }) : apiPost<AssessmentView>('/assessments', { ventureId, assessment }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: TESTS_KEY }), queryClient.invalidateQueries({ queryKey: QUESTIONS_KEY })]);
    },
  });
}

/** As a question: confirmed, the test leaves the list at once and comes back only if the server refuses. */
export function useArchiveTest() {
  const queryClient = useQueryClient();
  const lists = { queryKey: [...TESTS_KEY, 'list'] };
  return useMutation<unknown, ApiError, { readonly id: string; readonly version: number }, { readonly before: Snapshot<readonly AssessmentSummaryView[]> }>({
    mutationFn: ({ id, version }) => apiDelete(`/assessments/${id}`, { version }),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries(lists);
      const before = queryClient.getQueriesData<readonly AssessmentSummaryView[]>(lists);
      queryClient.setQueriesData<readonly AssessmentSummaryView[]>(lists, (tests) => tests?.filter((test) => test.id !== id));
      return { before };
    },
    onError: (_error, _variables, context) => restore(queryClient, context?.before),
    onSettled: () => queryClient.invalidateQueries({ queryKey: TESTS_KEY }),
  });
}

// --- A job's results, and one applicant's tests -----------------------------------------

export function resultsKey(jobId: string): readonly unknown[] {
  return [...RESULTS_KEY, 'job', jobId];
}

export function applicationTestsKey(applicationId: string): readonly unknown[] {
  return [...RESULTS_KEY, 'application', applicationId];
}

export function useJobResults(jobId: string): UseQueryResult<AssessmentResultsView, ApiError> {
  return useQuery({
    queryKey: resultsKey(jobId),
    queryFn: () => apiGet<AssessmentResultsView>(`/jobs/${jobId}/assessment-results`),
    placeholderData: (previous) => previous,
  });
}

export interface ApplicationTestsView {
  readonly testName: string | null;
  readonly isOffline: boolean;
  readonly attempts: readonly AttemptSummaryView[];
  readonly nextAction: TestAction | null;
  readonly attemptsUsed: number;
  readonly attemptsAllowed: number;
}

export function useApplicationTests(applicationId: string, isEnabled = true): UseQueryResult<ApplicationTestsView, ApiError> {
  return useQuery({
    queryKey: applicationTestsKey(applicationId),
    queryFn: () => apiGet<ApplicationTestsView>(`/applications/${applicationId}/assessment`),
    placeholderData: (previous) => previous,
    enabled: isEnabled,
  });
}

/** F9.03 by hand — a first test, a new link, or a retake; the server decides which is due. */
export function useSendTest() {
  const queryClient = useQueryClient();
  return useMutation<SentTestView, ApiError, { readonly applicationId: string; readonly version: number }>({
    mutationFn: ({ applicationId, version }) => apiPost<SentTestView>(`/applications/${applicationId}/assessment`, { version }),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: RESULTS_KEY }), queryClient.invalidateQueries({ queryKey: ['applications'] })]);
    },
  });
}

/** "Copy link" (S17 Q3): asks for the link, which logs the copy, and puts it on the clipboard. */
export async function copyTestLink(attemptId: string): Promise<string> {
  const { linkUrl } = await apiPost<{ readonly linkUrl: string }>(`/assessment-attempts/${attemptId}/link`);
  await navigator.clipboard.writeText(linkUrl);
  return linkUrl;
}

// --- F9.18: a result typed in by hand (ADR 0047) ----------------------------------------

interface RecordVariables {
  readonly row: { readonly applicationId: string };
  readonly input: RecordResultInput;
  readonly scoresheet: File | null;
}

/** The row as it will look once saved, shown the moment Save is pressed (CLAUDE.md §5a rule 1). */
function provisional(input: RecordResultInput, passPercent: number): AttemptSummaryView {
  const scorePercent = Math.round((input.score / input.outOf) * 10_000) / 100;
  return {
    id: 'pending',
    attemptNumber: 0,
    status: 'manually_graded',
    testName: input.testName,
    sentAt: new Date().toISOString(),
    expiresAt: null,
    startedAt: null,
    submittedAt: `${input.takenOn}T00:00:00Z`,
    minutesTaken: null,
    scoreRaw: input.score,
    scoreMax: input.outOf,
    scorePercent,
    passPercent,
    didPass: input.score * 100 >= passPercent * input.outOf,
    hasPendingManualGrading: false,
    graderName: null,
    sections: [],
    isLinkOpen: false,
    examSession: null,
    recorded: { how: input.how, platform: input.how === 'elsewhere' ? input.platform : null, takenOn: input.takenOn, scoresheetDocumentId: null },
    version: 0,
  };
}

/** Puts one row's latest result in the cached results, returning what was there for a rollback. */
function patchRow(queryClient: ReturnType<typeof useQueryClient>, jobId: string, applicationId: string, latest: AttemptSummaryView): AssessmentResultsView | undefined {
  const before = queryClient.getQueryData<AssessmentResultsView>(resultsKey(jobId));
  if (!before) return undefined;
  queryClient.setQueryData<AssessmentResultsView>(resultsKey(jobId), {
    ...before,
    rows: before.rows.map((row) => (row.applicationId === applicationId ? { ...row, latest, canRecord: false } : row)),
  });
  return before;
}

export function useRecordResult(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<AttemptSummaryView, ApiError, RecordVariables, { readonly before: AssessmentResultsView | undefined }>({
    mutationFn: async ({ row, input, scoresheet }) => {
      const body = new FormData();
      body.append(RECORDED_RESULT_FIELD, JSON.stringify(input));
      if (scoresheet) body.append(SCORESHEET_FIELD, scoresheet);
      return (await request<AttemptSummaryView>(`/applications/${row.applicationId}/assessment/result`, { method: 'POST', body })).data;
    },
    onMutate: async ({ row, input }) => {
      await queryClient.cancelQueries({ queryKey: resultsKey(jobId) });
      const passPercent = queryClient.getQueryData<AssessmentResultsView>(resultsKey(jobId))?.passPercent ?? 0;
      return { before: patchRow(queryClient, jobId, row.applicationId, provisional(input, passPercent)) };
    },
    onError: (_error, _variables, context) => {
      if (context?.before) queryClient.setQueryData(resultsKey(jobId), context.before);
    },
    // Held until the refetch lands, so the row never jumps back (CLAUDE.md §5a rule 3).
    onSettled: () => Promise.all([queryClient.invalidateQueries({ queryKey: RESULTS_KEY }), queryClient.invalidateQueries({ queryKey: ['applications'] })]),
  });
}

export function useCorrectResult(jobId: string) {
  const queryClient = useQueryClient();
  return useMutation<AttemptSummaryView, ApiError, { readonly applicationId: string; readonly latest: AttemptSummaryView; readonly input: CorrectResultInput }, { readonly before: AssessmentResultsView | undefined }>({
    mutationFn: ({ latest, input }) => apiPut<AttemptSummaryView>(`/assessment-attempts/${latest.id}/result`, input),
    onMutate: async ({ applicationId, latest, input }) => {
      await queryClient.cancelQueries({ queryKey: resultsKey(jobId) });
      const passPercent = latest.passPercent ?? 0;
      const scorePercent = Math.round((input.score / input.outOf) * 10_000) / 100;
      const next = { ...latest, scoreRaw: input.score, scoreMax: input.outOf, scorePercent, didPass: input.score * 100 >= passPercent * input.outOf };
      return { before: patchRow(queryClient, jobId, applicationId, next) };
    },
    onError: (_error, _variables, context) => {
      if (context?.before) queryClient.setQueryData(resultsKey(jobId), context.before);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: RESULTS_KEY }),
  });
}
